import React, { useCallback, useEffect, useState } from 'react';
import type { CreativeCreditInput } from 'sr-types/lib/person/v1/graphql';
import CreativeCreditEditor from '../../../../person/detailsPage/editComponents/CreativeCreditEditor';
import { EMPTY_CREATIVE_CREDIT } from '../../../../person/person';
import { createCreativeCreditInputObject, useSaveCreativeCredits } from '../../../components/person/person';
import { useI18n } from '../../../i18n/I18n';
import { failedToastMessage, successToastMessage } from '../../../utils/commonUtils';
import { MANUAL_PROFILE_UPDATE_MODE_OPTIONS, MANUAL_PROFILE_UPDATE_MODE_TOKENS } from '../../constants';
import { saveCreditsToCandidateProfile } from '../../data/profileUpdate';
import { AddKeyCreditModalProps, ProfileUpdateMode } from '../../types';
import { getKeyCreditsPersonId, isProfileEligibleForCreditUpdate } from '../utils';
import ProfileUpdateModeSelect from './internal/ProfileUpdateModeSelect';

const buildInitialState = (productionName: string): CreativeCreditInput => ({
    ...EMPTY_CREATIVE_CREDIT,
    showName: productionName
});

export default function AddKeyCreditModal({
    isOpen,
    initialProductionName,
    onClose,
    onAdd,
    lineItemRow
}: AddKeyCreditModalProps) {
    const t = useI18n();
    const [createState, setCreateState] = useState(() => buildInitialState(initialProductionName));
    const [profileUpdateMode, setProfileUpdateMode] = useState<ProfileUpdateMode>('all');
    const [isCreativeCreditSaving, setIsCreativeCreditSaving] = useState(false);
    const { saveCreativeCredits } = useSaveCreativeCredits();

    const isProfileEligible = isProfileEligibleForCreditUpdate(lineItemRow);
    const personId = getKeyCreditsPersonId(lineItemRow?.itemReference);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setCreateState(buildInitialState(initialProductionName));
        setProfileUpdateMode('all');
    }, [initialProductionName, isOpen]);

    const handleAdd = useCallback(async () => {
        const newCredit = createCreativeCreditInputObject(createState);
        onAdd(newCredit);

        try {
            setIsCreativeCreditSaving(true);
            const profileUpdated = await saveCreditsToCandidateProfile({
                personId,
                isProfileEligible,
                profileUpdateMode,
                creditsToMerge: [newCredit],
                saveCreativeCredits
            });
            if (profileUpdated) {
                successToastMessage('shortlist.key.credits.profile.update.success');
            }
        } catch {
            failedToastMessage('shortlist.key.credits.ai.profile.update.error');
        } finally {
            setIsCreativeCreditSaving(false);
            setProfileUpdateMode('all');
            onClose();
        }
    }, [createState, isProfileEligible, onAdd, onClose, personId, profileUpdateMode, saveCreativeCredits]);

    return (
        <CreativeCreditEditor
            createState={createState}
            setCreateState={setCreateState}
            isAddCreativeCreditsModalOpen={isOpen}
            onClose={onClose}
            onSave={handleAdd}
            isCreativeCreditSaving={isCreativeCreditSaving}
            title={t('shortlist.line.item.key.credits.add.title')}
            saveLabel={t('common.add')}
            disableTypeField={false}
            modalBodyAccessory={
                isProfileEligible ? (
                    <ProfileUpdateModeSelect
                        value={profileUpdateMode}
                        onChange={setProfileUpdateMode}
                        options={MANUAL_PROFILE_UPDATE_MODE_OPTIONS}
                        tokenOverrides={MANUAL_PROFILE_UPDATE_MODE_TOKENS}
                    />
                ) : null
            }
        />
    );
}
